/**
 * Shuffles the UGC marquee's card order on each page load, then decides whether
 * the marquee should actually animate. Also wires up each card's:
 *  - like/dislike buttons (photo cards only), as a purely client-side, optimistic
 *    helpful-vote toggle — there's no backend to persist votes against, so a click
 *    just adjusts the count
 *  - review text (testimonial cards only), clamped to 2 lines by default (see
 *    `.ugc-gallery__text` in the section's stylesheet) and expandable in place on
 *    click/Enter/Space
 *  - testimonial content itself: the section server-renders one deterministic
 *    name/quote pairing per testimonial card (see ugc-gallery.liquid) as a no-JS
 *    fallback, then this re-picks a random one from the full reviewer pool (embedded
 *    as JSON) so repeat visits don't always see the same handful of quotes
 *
 * The marquee's seamless infinite loop (see the section's CSS `translateX(-50%)` keyframe)
 * requires two identical groups of items back to back. Liquid has no random/shuffle filter,
 * so this reorders the first group's items (Fisher-Yates) and then mirrors that order into
 * the second, `aria-hidden`+`inert` duplicate group so the loop stays seamless. The click/keydown
 * listeners above are delegated on the component itself, but `inert` on the duplicate group
 * already blocks pointer/keyboard interaction there, so votes and expand/collapse only ever
 * register on the primary copy.
 *
 * When there are fewer cards than fit in a single row at the current viewport width, looping
 * just repeats a near-empty strip, so the duplicate group is hidden and the track is switched
 * to a static, centered row instead (see `.ugc-gallery--static` in the section's stylesheet).
 * The column count per breakpoint mirrors the `--ugc-gallery-columns-*` custom properties set
 * inline by the section, and the breakpoints themselves mirror its `.ugc-gallery__item` media queries.
 *
 * The marquee is hidden (opacity 0) until all of the above finishes, then this adds
 * `ugc-gallery--ready` to reveal it — otherwise the server-rendered, pre-shuffle order
 * would flash on screen for a moment before JS reorders it.
 */
const BREAKPOINTS = [
  { query: '(min-width: 1400px)', property: '--ugc-gallery-columns-xxl' },
  { query: '(min-width: 1200px)', property: '--ugc-gallery-columns-xl' },
  { query: '(min-width: 990px)', property: '--ugc-gallery-columns-l' },
  { query: '(min-width: 750px)', property: '--ugc-gallery-columns-m' },
];
const DEFAULT_COLUMNS_PROPERTY = '--ugc-gallery-columns-s';

// Mirrors the section's own Liquid logic (first letter of the first two words) so a
// randomly-picked reviewer still gets a matching avatar.
function initialsOf(name) {
  const [first = '', last = ''] = name.split(' ');
  return `${first.charAt(0)}${last.charAt(0)}`;
}

class UgcGalleryComponent extends HTMLElement {
  #mediaQueryLists = [];
  #itemCount = 0;
  #duplicateGroup = null;
  #onChange = () => this.#updateLayout();

  connectedCallback() {
    const [group, duplicateGroup] = this.querySelectorAll('.ugc-gallery__group');
    if (!group) return;

    const items = Array.from(group.children);

    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }

    // `appendChild` on a node already in the document moves it rather than cloning it.
    for (const item of items) {
      group.appendChild(item);
    }

    this.#randomizeTestimonials(group);

    if (duplicateGroup) {
      duplicateGroup.innerHTML = group.innerHTML;
    }

    this.#itemCount = items.length;
    this.#duplicateGroup = duplicateGroup ?? null;

    this.#mediaQueryLists = BREAKPOINTS.map(({ query }) => window.matchMedia(query));
    for (const mql of this.#mediaQueryLists) {
      mql.addEventListener('change', this.#onChange);
    }

    this.#updateLayout();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    // Card order (and, for testimonial cards, which reviewer shows) is now final —
    // reveal the marquee (see `.ugc-gallery__marquee`'s opacity in the section's
    // stylesheet), which was hidden up to this point to mask the server-rendered,
    // pre-shuffle order from flashing on screen first.
    this.classList.add('ugc-gallery--ready');
  }

  disconnectedCallback() {
    for (const mql of this.#mediaQueryLists) {
      mql.removeEventListener('change', this.#onChange);
    }
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  // Swaps each testimonial card's server-rendered name/quote for a random pick from the
  // full reviewer pool (embedded by the section as JSON). Picks without replacement
  // (shuffle-then-take) so the same reviewer can't appear twice in one render.
  #randomizeTestimonials(group) {
    const cards = group.querySelectorAll('.ugc-gallery__item--solid');
    if (!cards.length) return;

    const source = this.querySelector('[data-ugc-gallery-reviews]');
    if (!source) return;

    let pool;
    try {
      pool = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(pool) || pool.length === 0) return;

    pool = pool.slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    cards.forEach((card, index) => {
      const review = pool[index % pool.length];
      const textEl = card.querySelector('.ugc-gallery__text');
      const nameEl = card.querySelector('.ugc-gallery__name');
      const avatarEl = card.querySelector('.ugc-gallery__avatar');
      if (textEl) textEl.textContent = review.quote;
      if (nameEl) nameEl.textContent = review.name;
      if (avatarEl) avatarEl.textContent = initialsOf(review.name);
    });
  }

  #onClick = (event) => {
    const voteButton = event.target.closest('.ugc-gallery__vote');
    if (voteButton) {
      this.#toggleVote(voteButton);
      return;
    }

    const text = event.target.closest('.ugc-gallery__text');
    if (text) this.#toggleExpanded(text);
  };

  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const text = event.target.closest?.('.ugc-gallery__text');
    if (!text) return;
    // Prevent the page from scrolling on Space, and the browser from
    // "clicking" the element a second time when this synthesizes one.
    event.preventDefault();
    this.#toggleExpanded(text);
  };

  #toggleExpanded(text) {
    const isExpanded = text.classList.toggle('is-expanded');
    text.setAttribute('aria-expanded', String(isExpanded));
  }

  #toggleVote(button) {
    const actions = button.closest('.ugc-gallery__actions');
    const other = actions?.querySelector('.ugc-gallery__vote.is-active');
    if (other && other !== button) {
      this.#adjustVote(other, -1);
      other.classList.remove('is-active');
      other.setAttribute('aria-pressed', 'false');
    }

    const isActive = button.classList.toggle('is-active');
    button.setAttribute('aria-pressed', String(isActive));
    this.#adjustVote(button, isActive ? 1 : -1);
  }

  #adjustVote(button, delta) {
    const countEl = button.querySelector('.ugc-gallery__vote-count');
    if (!countEl) return;
    countEl.textContent = String(parseInt(countEl.textContent, 10) + delta);
  }

  #updateLayout() {
    const breakpoint = BREAKPOINTS.find(({ query }) => window.matchMedia(query).matches);
    const property = breakpoint ? breakpoint.property : DEFAULT_COLUMNS_PROPERTY;
    const columns = parseInt(getComputedStyle(this).getPropertyValue(property), 10) || 1;
    const isStatic = this.#itemCount <= columns;

    this.classList.toggle('ugc-gallery--static', isStatic);
    if (this.#duplicateGroup) {
      this.#duplicateGroup.hidden = isStatic;
    }
  }
}

if (!customElements.get('ugc-gallery-component')) {
  customElements.define('ugc-gallery-component', UgcGalleryComponent);
}
